import API_BASE from '../config';
const API_URL = API_BASE;

// Simple in-memory cache with TTL
const cache = {
  data: {},
  timestamps: {},
  TTL: 30000
};

const getCached = (key) => {
  const timestamp = cache.timestamps[key];
  if (timestamp && Date.now() - timestamp < cache.TTL) {
    return cache.data[key];
  }
  return null;
};

const setCache = (key, value) => {
  cache.data[key] = value;
  cache.timestamps[key] = Date.now();
};

const clearCache = () => {
  cache.data = {};
  cache.timestamps = {};
};

// ===========================================
// AUTH HELPERS
// Token lives in an HttpOnly cookie set by the server.
// JS never touches the token — only a session flag is stored here.
// ===========================================

export const setAuthFlag = () => {
  localStorage.setItem('admin_authenticated', '1');
};

export const clearAuthFlag = () => {
  localStorage.removeItem('admin_authenticated');
};

export const isAuthenticated = () => {
  return localStorage.getItem('admin_authenticated') === '1';
};

// ===========================================
// API HELPERS
// All requests include credentials: 'include' so the browser sends
// the HttpOnly cookie automatically. No Authorization header needed.
// ===========================================

const defaultOptions = {
  credentials: 'include',
  headers: { 'Content-Type': 'application/json' }
};

const authHeaders = () => ({
  'Content-Type': 'application/json'
});

const handleResponse = async (response) => {
  if (response.status === 401) {
    clearAuthFlag();
    window.location.href = '/admin/login';
    throw new Error('Session expired');
  }

  // Clone before reading so "body already read" from any script interceptor never crashes us
  let data = null;
  try {
    data = await response.clone().json();
  } catch {
    // Response is not JSON (e.g. Cloudflare 520 HTML error page)
    if (!response.ok) {
      throw new Error(`Server error (${response.status}) — please try again in a moment`);
    }
  }

  if (!response.ok) {
    throw new Error(formatDetail(data) || 'Request failed');
  }

  return data;
};

// FastAPI's `detail` can be a string OR an array of {loc, msg, type} objects
// (validation errors). Coerce both into a readable single string.
const formatDetail = (data) => {
  if (!data) return '';
  const d = data.detail;
  if (!d) return '';
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d
      .map(e => (typeof e === 'string' ? e : (e && e.msg) || JSON.stringify(e)))
      .join('; ');
  }
  if (typeof d === 'object') return d.msg || JSON.stringify(d);
  return String(d);
};

// ===========================================
// AUTH API
// ===========================================

export const adminLogin = async (email, password) => {
  // Login is the ONE endpoint where a 401 means "wrong credentials", NOT
  // "session expired". We deliberately bypass handleResponse here so we can
  // surface the backend's actual error message (e.g. "Invalid email or password")
  // instead of a misleading "Session expired" message + auto-redirect loop.
  const response = await fetch(`${API_URL}/api/admin/login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password })
  });

  let data = null;
  try {
    data = await response.clone().json();
  } catch {
    // Non-JSON response (e.g. Cloudflare 520 HTML page)
    if (!response.ok) {
      throw new Error(`Server error (${response.status}) — please try again in a moment`);
    }
  }

  if (response.status === 401) {
    throw new Error((data && data.detail) || 'Invalid email or password');
  }
  if (!response.ok) {
    throw new Error((data && data.detail) || 'Login failed. Please try again.');
  }

  setAuthFlag();
  return data;
};

export const adminLogout = async () => {
  await fetch(`${API_URL}/api/admin/logout`, {
    method: 'POST',
    credentials: 'include'
  }).catch(() => {});
  clearAuthFlag();
  clearCache();
  window.location.href = '/admin/login';
};

export const getCurrentAdmin = async () => {
  const response = await fetch(`${API_URL}/api/admin/me`, {
    credentials: 'include',
    headers: authHeaders()
  });
  return handleResponse(response);
};

// ===========================================
// ANALYTICS API
// ===========================================

export const getAnalyticsSummary = async () => {
  const response = await fetch(`${API_URL}/api/analytics/summary`, {
    credentials: 'include',
    headers: authHeaders()
  });
  return handleResponse(response);
};

export const getAnalytics = async (forceRefresh = false) => {
  const cacheKey = 'analytics';
  if (!forceRefresh) {
    const cached = getCached(cacheKey);
    if (cached) return cached;
  }

  const response = await fetch(`${API_URL}/api/admin/analytics`, {
    credentials: 'include',
    headers: authHeaders()
  });
  const data = await handleResponse(response);
  setCache(cacheKey, data);
  return data;
};

// ===========================================
// ORDERS API
// ===========================================

// The backend sends `bucket_ids` (arrays of order IDs) instead of `buckets`
// (arrays of full order objects), because every order appears in exactly one
// bucket and serialising the objects twice doubled the response — 1.73MB for
// 67 orders, half of it a byte-for-byte duplicate of the `orders` array.
//
// We rebuild `buckets` here, pointing at the same objects already in `orders`,
// so every caller (Dashboard, InProgress, Completed, RecruiterQueue,
// OrderReceipt) keeps consuming the exact shape it always has.
const hydrateBuckets = (data) => {
  if (!data || !data.bucket_ids) return data;
  const byId = new Map((data.orders || []).map(o => [o.id, o]));
  const buckets = {};
  for (const [name, ids] of Object.entries(data.bucket_ids)) {
    buckets[name] = ids.map(id => byId.get(id)).filter(Boolean);
  }
  return { ...data, buckets };
};

export const getOrders = async (stage = null, industry = null, forceRefresh = false) => {
  const cacheKey = `orders_${stage || 'all'}_${industry || 'all'}`;
  if (!forceRefresh) {
    const cached = getCached(cacheKey);
    if (cached) return cached;
  }

  const params = new URLSearchParams();
  if (stage) params.append('stage', stage);
  if (industry) params.append('industry', industry);

  const response = await fetch(`${API_URL}/api/admin/orders?${params}`, {
    credentials: 'include',
    headers: authHeaders()
  });
  const data = hydrateBuckets(await handleResponse(response));
  setCache(cacheKey, data);
  return data;
};

export const getOrder = async (orderId) => {
  const response = await fetch(`${API_URL}/api/admin/orders/${orderId}`, {
    credentials: 'include',
    headers: authHeaders()
  });
  return handleResponse(response);
};

// Cheap poll companion to getOrder — returns only {stage, ai_rewrite_status, …}.
// Used by the Generate-profile poll loop, which fires every 3s and only ever
// reads ai_rewrite_status. getOrder costs 7 sequential DB round-trips and
// ships the full ~40KB order row; this costs one indexed row read. See the
// backend docstring on GET /orders/{id}/status for why that mattered.
export const getOrderStatus = async (orderId) => {
  const response = await fetch(`${API_URL}/api/admin/orders/${orderId}/status`, {
    credentials: 'include',
    headers: authHeaders()
  });
  return handleResponse(response);
};

export const getOrderCounts = async (days = 30, { dateFrom = '', dateTo = '' } = {}) => {
  const params = new URLSearchParams();
  // dateFrom/dateTo (explicit range or single date) win over days on the backend
  if (dateFrom || dateTo) {
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
  } else if (days > 0) {
    params.set('days', days);
  }
  const qs = params.toString();
  const response = await fetch(`${API_URL}/api/admin/orders/counts${qs ? '?' + qs : ''}`, {
    credentials: 'include',
    headers: authHeaders()
  });
  return handleResponse(response);
};

export const getFilteredOrders = async ({ days = 30, dateFrom = '', dateTo = '', filterGroup = '', search = '' } = {}) => {
  const params = new URLSearchParams();
  if (dateFrom || dateTo) {
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
  } else if (days > 0) {
    params.set('days', days);
  }
  if (filterGroup && filterGroup !== 'all') params.set('filter_group', filterGroup);
  if (search) params.set('search', search);
  const qs = params.toString();
  const response = await fetch(`${API_URL}/api/admin/orders${qs ? '?' + qs : ''}`, {
    credentials: 'include',
    headers: authHeaders()
  });
  return hydrateBuckets(await handleResponse(response));
};

// Builds the Orders-tab "download as Excel" URL — same filters as the list
// view (via getFilteredOrders), so the download always matches what's on
// screen. Not a fetch: admin auth is an HttpOnly cookie, so a plain browser
// navigation to this URL (see utils/download.js) authenticates itself; no
// need to fetch+blob it in JS.
export const getOrdersExportUrl = ({ days = 30, dateFrom = '', dateTo = '', filterGroup = '', search = '' } = {}) => {
  const params = new URLSearchParams();
  if (dateFrom || dateTo) {
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
  } else if (days > 0) {
    params.set('days', days);
  }
  if (filterGroup && filterGroup !== 'all') params.set('filter_group', filterGroup);
  if (search) params.set('search', search);
  const qs = params.toString();
  return `${API_URL}/api/admin/orders/export${qs ? '?' + qs : ''}`;
};

export const getResumeUrl = async (orderId) => {
  const response = await fetch(`${API_URL}/api/admin/orders/${orderId}/resume-url`, {
    credentials: 'include',
    headers: authHeaders()
  });
  return handleResponse(response);
};

export const updateOrder = async (orderId, data) => {
  const response = await fetch(`${API_URL}/api/admin/orders/${orderId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: authHeaders(),
    body: JSON.stringify(data)
  });
  return handleResponse(response);
};

export const regenerateSection = async (orderId, section, instruction) => {
  const response = await fetch(`${API_URL}/api/admin/orders/${orderId}/regenerate-section`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders(),
    body: JSON.stringify({ section, instruction })
  });
  return handleResponse(response);
};

export const assignRecruiter = async (orderId, recruiterId) => {
  const response = await fetch(`${API_URL}/api/admin/orders/${orderId}/assign-recruiter`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders(),
    body: JSON.stringify({ recruiter_id: recruiterId })
  });
  return handleResponse(response);
};

export const createMagicLink = async (orderId) => {
  const response = await fetch(`${API_URL}/api/admin/orders/${orderId}/create-magic-link`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders()
  });
  return handleResponse(response);
};

export const sendMagicLink = async (orderId) => {
  const response = await fetch(`${API_URL}/api/admin/orders/${orderId}/send-magic-link`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders()
  });
  return handleResponse(response);
};

export const resendIntakeLink = async (orderId) => {
  const response = await fetch(`${API_URL}/api/admin/orders/${orderId}/resend-intake-link`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders()
  });
  return handleResponse(response);
};

export const deliverOrder = async (orderId) => {
  const response = await fetch(`${API_URL}/api/admin/orders/${orderId}/deliver`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders()
  });
  return handleResponse(response);
};

// Retries just the delivery email for an already-delivered order — for when
// deliverOrder's email_sent came back false (or an old order delivered
// before this was tracked at all).
export const resendDeliveryEmail = async (orderId) => {
  const response = await fetch(`${API_URL}/api/admin/orders/${orderId}/resend-delivery-email`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders()
  });
  return handleResponse(response);
};

// Manually re-sends the "Order confirmed" payment email — for a customer who
// says they never received it. Same template as the original auto-send.
export const resendConfirmationEmail = async (orderId) => {
  const response = await fetch(`${API_URL}/api/admin/orders/${orderId}/resend-confirmation-email`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders()
  });
  return handleResponse(response);
};

export const replyToComment = async (orderId, commentId, replyText) => {
  const response = await fetch(`${API_URL}/api/admin/orders/${orderId}/comments/${commentId}/reply`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders(),
    body: JSON.stringify({ comment_id: commentId, reply_text: replyText })
  });
  return handleResponse(response);
};

// ===========================================
// INBOX REPLY
// ===========================================

export const replyToInboxMessage = async (messageId, replyText) => {
  const response = await fetch(`${API_URL}/api/admin/inbox/${messageId}/reply`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders(),
    body: JSON.stringify({ reply_text: replyText })
  });
  return handleResponse(response);
};

// ===========================================
// CUSTOMER FEEDBACK
// ===========================================

export const submitFeedback = async (payload) => {
  // Public — no auth needed
  const response = await fetch(`${API_URL}/api/feedback/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });
  return handleResponse(response);
};

export const getFeedbacks = async () => {
  const response = await fetch(`${API_URL}/api/admin/feedback`, {
    credentials: 'include',
    headers: authHeaders()
  });
  return handleResponse(response);
};

export const getFeedback = async (fbId) => {
  const response = await fetch(`${API_URL}/api/admin/feedback/${fbId}`, {
    credentials: 'include',
    headers: authHeaders()
  });
  return handleResponse(response);
};

// ===========================================
// COUPONS API
// ===========================================

export const validateCoupon = async (code, basePrice) => {
  // Public — no auth needed
  const response = await fetch(`${API_URL}/api/coupons/validate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code, base_price: basePrice })
  });
  return handleResponse(response);
};

export const getCoupons = async () => {
  const response = await fetch(`${API_URL}/api/admin/coupons`, {
    credentials: 'include',
    headers: authHeaders()
  });
  return handleResponse(response);
};

export const createCoupon = async (data) => {
  const response = await fetch(`${API_URL}/api/admin/coupons`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders(),
    body: JSON.stringify(data)
  });
  return handleResponse(response);
};

export const updateCoupon = async (couponId, data) => {
  const response = await fetch(`${API_URL}/api/admin/coupons/${couponId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: authHeaders(),
    body: JSON.stringify(data)
  });
  return handleResponse(response);
};

export const deleteCoupon = async (couponId) => {
  const response = await fetch(`${API_URL}/api/admin/coupons/${couponId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: authHeaders()
  });
  return handleResponse(response);
};

// ===========================================
// CAMPAIGNS / MARKETING (UTM attribution)
// ===========================================

export const getCampaigns = async () => {
  const response = await fetch(`${API_URL}/api/admin/campaigns`, {
    credentials: 'include',
    headers: authHeaders()
  });
  return handleResponse(response);
};

export const createCampaign = async (data) => {
  const response = await fetch(`${API_URL}/api/admin/campaigns`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders(),
    body: JSON.stringify(data)
  });
  return handleResponse(response);
};

export const updateCampaign = async (campaignId, data) => {
  const response = await fetch(`${API_URL}/api/admin/campaigns/${campaignId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: authHeaders(),
    body: JSON.stringify(data)
  });
  return handleResponse(response);
};

export const deleteCampaign = async (campaignId) => {
  const response = await fetch(`${API_URL}/api/admin/campaigns/${campaignId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: authHeaders()
  });
  return handleResponse(response);
};

// Customer Name | UTM Source | UTM Campaign | Revenue earned | UTM Content | Evaluation taken
export const getCampaignAttribution = async (days = 90, includeInternal = false) => {
  const params = new URLSearchParams({ days: String(days), include_internal: String(includeInternal) });
  const response = await fetch(`${API_URL}/api/admin/campaigns/attribution?${params}`, {
    credentials: 'include',
    headers: authHeaders()
  });
  return handleResponse(response);
};

// Logs a tracking link the moment it's copied — audit trail of exactly
// which link was issued, independent of whether it's ever clicked. Never
// blocks the copy action itself if this fails (see callers).
export const recordCampaignLink = async (campaignId, { source, term, full_url }) => {
  const response = await fetch(`${API_URL}/api/admin/campaigns/${campaignId}/links`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders(),
    body: JSON.stringify({ source, term, full_url })
  });
  return handleResponse(response);
};

// ===========================================
// REVENUE
// ===========================================

export const getRevenue = async () => {
  const response = await fetch(`${API_URL}/api/admin/revenue`, {
    credentials: 'include',
    headers: authHeaders()
  });
  return handleResponse(response);
};

// Free-evaluation funnel analytics. One call returns every section the
// Analytics tab renders — funnel, segments, timing, messaging — so the page
// makes a single round trip instead of five.
export const getAnalyticsOverview = async (days = 90, includeInternal = false) => {
  const params = new URLSearchParams({
    days: String(days),
    include_internal: String(includeInternal),
  });
  const response = await fetch(`${API_URL}/api/admin/analytics/overview?${params}`, {
    credentials: 'include',
    headers: authHeaders()
  });
  return handleResponse(response);
};

// Shared by the evaluations table and its CSV export so the file can never
// be built from a different filter set than the screen.
const evaluationParams = (filters = {}) => {
  const params = new URLSearchParams();
  params.set('days', String(filters.days ?? 90));
  params.set('include_internal', String(filters.includeInternal ?? false));
  ['converted', 'industry', 'score_band', 'experience_band', 'region', 'report_opened', 'source']
    .forEach((k) => { if (filters[k]) params.set(k, filters[k]); });
  return params;
};

// Raw one-row-per-evaluation table.
export const getEvaluations = async (filters = {}) => {
  const response = await fetch(
    `${API_URL}/api/admin/analytics/evaluations?${evaluationParams(filters)}`,
    { credentials: 'include', headers: authHeaders() }
  );
  return handleResponse(response);
};

// URL builder, not a fetch — see getOrdersExportUrl for why (cookie auth means
// a plain navigation is already authenticated).
export const getEvaluationsExportUrl = (filters = {}) =>
  `${API_URL}/api/admin/analytics/evaluations/export?${evaluationParams(filters)}`;

// Revenue-tab "download as Excel" URL — see getOrdersExportUrl for why this
// isn't a fetch call.
export const getRevenueExportUrl = () => `${API_URL}/api/admin/revenue/export`;

// ===========================================
// PRICING API
// ===========================================

export const getPricingConfig = async () => {
  const response = await fetch(`${API_URL}/api/admin/pricing/`, {
    credentials: 'include',
    headers: authHeaders()
  });
  return handleResponse(response);
};

export const updatePricingConfig = async (data) => {
  const response = await fetch(`${API_URL}/api/admin/pricing/`, {
    method: 'PUT',
    credentials: 'include',
    headers: authHeaders(),
    body: JSON.stringify(data)
  });
  return handleResponse(response);
};

// ===========================================
// RECRUITERS API
// ===========================================

// 30-second client-side cache — same pattern as getOrders/getAnalytics so the
// Recruiters page renders instantly on repeat visits and tab switches.
export const getRecruiters = async (forceRefresh = false) => {
  const cacheKey = 'recruiters_all';
  if (!forceRefresh) {
    const cached = getCached(cacheKey);
    if (cached) return cached;
  }
  const response = await fetch(`${API_URL}/api/admin/recruiters`, {
    credentials: 'include',
    headers: authHeaders()
  });
  const data = await handleResponse(response);
  setCache(cacheKey, data);
  return data;
};

// Helper: invalidate the recruiters list cache after any mutation so the next
// load shows the fresh data instead of a stale snapshot.
const invalidateRecruitersCache = () => {
  delete cache.data['recruiters_all'];
  delete cache.timestamps['recruiters_all'];
};

export const createRecruiter = async (data) => {
  const response = await fetch(`${API_URL}/api/admin/recruiters`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders(),
    body: JSON.stringify(data)
  });
  const out = await handleResponse(response);
  invalidateRecruitersCache();
  return out;
};

export const updateRecruiter = async (recruiterId, data) => {
  const response = await fetch(`${API_URL}/api/admin/recruiters/${recruiterId}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: authHeaders(),
    body: JSON.stringify(data)
  });
  const out = await handleResponse(response);
  invalidateRecruitersCache();
  return out;
};

export const deleteRecruiter = async (recruiterId) => {
  const response = await fetch(`${API_URL}/api/admin/recruiters/${recruiterId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: authHeaders()
  });
  const out = await handleResponse(response);
  invalidateRecruitersCache();
  return out;
};

export const approveRecruiter = async (recruiterId) => {
  const response = await fetch(`${API_URL}/api/admin/recruiters/${recruiterId}/approve`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders()
  });
  const out = await handleResponse(response);
  invalidateRecruitersCache();
  return out;
};

export const rejectRecruiter = async (recruiterId) => {
  const response = await fetch(`${API_URL}/api/admin/recruiters/${recruiterId}/reject`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders()
  });
  const out = await handleResponse(response);
  invalidateRecruitersCache();
  return out;
};

export const setRecruiterPassword = async (recruiterId, password) => {
  const response = await fetch(`${API_URL}/api/admin/recruiters/${recruiterId}/set-password`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders(),
    body: JSON.stringify({ password })
  });
  return handleResponse(response);
};

export const getRecruitersByStatus = async (status) => {
  const response = await fetch(`${API_URL}/api/admin/recruiters?status=${status}`, {
    credentials: 'include',
    headers: authHeaders()
  });
  return handleResponse(response);
};

// ===========================================
// PUBLIC API (No auth required)
// ===========================================

export const validateReviewLink = async (token) => {
  const response = await fetch(`${API_URL}/api/review/${token}`);
  return handleResponse(response);
};

export const submitRecruiterEdit = async (token, data) => {
  const response = await fetch(`${API_URL}/api/review/${token}/edit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  return handleResponse(response);
};

// Step 10e Change 4: token-authenticated signed URL for the intake resume.
export const getReviewResumeUrl = async (token) => {
  const response = await fetch(`${API_URL}/api/review/${token}/resume-url`);
  return handleResponse(response);
};

// Step 10f Fix 4: candidate delivery page payload (token = order UUID).
export const getDeliveryPayload = async (token) => {
  const response = await fetch(`${API_URL}/api/delivery/${token}`);
  return handleResponse(response);
};

// 100-day action plan. Same auth as the delivery payload above: the order
// UUID is the credential, and the backend gates on stage=='delivered'.
export const getActionPlan = async (token) => {
  const response = await fetch(`${API_URL}/api/action-plan/${token}`);
  return handleResponse(response);
};

export const saveActionPlan = async (token, progress, stars) => {
  const response = await fetch(`${API_URL}/api/action-plan/${token}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ progress, stars })
  });
  return handleResponse(response);
};

// Unload-time save. A normal fetch is abandoned when the page goes away;
// sendBeacon is queued by the browser and delivered out-of-band. It can only
// POST, so the backend accepts POST on this path as an alias for PUT.
export const beaconActionPlan = (token, progress, stars) => {
  if (!navigator.sendBeacon) return false;
  const blob = new Blob([JSON.stringify({ progress, stars })], { type: 'application/json' });
  return navigator.sendBeacon(`${API_URL}/api/action-plan/${token}`, blob);
};

// Admin usage snapshot across every customer's plan — cookie-authenticated,
// unlike the three functions above which use the order UUID itself as the
// credential.
export const getActionPlanAdmin = async () => {
  const response = await fetch(`${API_URL}/api/admin/action-plan/`, {
    credentials: 'include',
    headers: authHeaders()
  });
  return handleResponse(response);
};

export const certifyProfile = async (token, sectionsReviewed) => {
  const response = await fetch(`${API_URL}/api/review/${token}/certify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sections_reviewed: sectionsReviewed })
  });
  return handleResponse(response);
};

export const finalizeReview = async (token, edits, notes) => {
  const response = await fetch(`${API_URL}/api/review/${token}/finalize`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ edits: edits || {}, notes: notes || {} })
  });
  return handleResponse(response);
};

export const createOrder = async (data) => {
  const params = new URLSearchParams(data);
  const response = await fetch(`${API_URL}/api/orders/create?${params}`, {
    method: 'POST'
  });
  return handleResponse(response);
};
